// SLAPolicyList.js - SLA Policy Management

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Button, Chip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, FormControl, InputLabel, Select, FormControlLabel, Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../utils/permissionUtils';
import { slaPolicyService } from './helpDeskService';
import { TicketPriorityLabels, TicketPriorityColors, TicketPriorityOptions, formatDuration } from './helpDeskEnums';

const SLAPolicyList = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [policies, setPolicies] = useState([]);
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  // Form states
  const [formOpen, setFormOpen] = useState(false);
  const [editPolicy, setEditPolicy] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    priority: 'MEDIUM',
    first_response_hours: 8,
    resolution_hours: 48,
    warning_threshold_percent: 80,
    is_active: true,
    is_default: false
  });
  const [submitting, setSubmitting] = useState(false);

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null);

  useEffect(() => {
    document.title = 'SLA Policies | Help Desk';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      loadPolicies();
    }
  }, [canView, chatbotId]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
      if (!viewPerm) {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error checking permissions:', err);
      setLoading(false);
    }
  };

  const loadPolicies = async () => {
    setLoading(true);
    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      const response = await slaPolicyService.getAll(params);
      if (response.success) {
        setPolicies(response.data || []);
      }
    } catch (err) {
      enqueueSnackbar('Failed to load SLA policies', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (policy = null) => {
    if (policy) {
      setEditPolicy(policy);
      setFormData({
        name: policy.name || '',
        description: policy.description || '',
        priority: policy.priority || 'MEDIUM',
        first_response_hours: policy.first_response_hours || 8,
        resolution_hours: policy.resolution_hours || 48,
        warning_threshold_percent: policy.warning_threshold_percent || 80,
        is_active: policy.is_active !== false,
        is_default: policy.is_default || false
      });
    } else {
      setEditPolicy(null);
      setFormData({
        name: '',
        description: '',
        priority: 'MEDIUM',
        first_response_hours: 8,
        resolution_hours: 48,
        warning_threshold_percent: 80,
        is_active: true,
        is_default: false
      });
    }
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditPolicy(null);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      enqueueSnackbar('Policy name is required', { variant: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        ...formData,
        chatbot_id: chatbotId
      };

      let response;
      if (editPolicy) {
        response = await slaPolicyService.update(editPolicy.id, data);
      } else {
        response = await slaPolicyService.create(data);
      }

      if (response.success) {
        enqueueSnackbar(
          editPolicy ? 'Policy updated successfully' : 'Policy created successfully',
          { variant: 'success' }
        );
        handleCloseForm();
        loadPolicies();
      } else {
        enqueueSnackbar(response.message || 'Failed to save policy', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to save policy', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (policy) => {
    setPolicyToDelete(policy);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!policyToDelete) return;

    try {
      const response = await slaPolicyService.delete(policyToDelete.id);
      if (response.success) {
        enqueueSnackbar('Policy deleted successfully', { variant: 'success' });
        loadPolicies();
      } else {
        enqueueSnackbar(response.message || 'Failed to delete policy', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to delete policy', { variant: 'error' });
    } finally {
      setDeleteConfirmOpen(false);
      setPolicyToDelete(null);
    }
  };

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: 10, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view SLA policies.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm/helpdesk`)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              SLA Policies
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Define service level agreements for different priority levels
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <IconButton onClick={loadPolicies} disabled={loading}>
            <RefreshIcon />
          </IconButton>
          {canModify && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenForm()}
            >
              Add Policy
            </Button>
          )}
        </Box>
      </Box>

      {/* Policies Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Policy Name</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>First Response</TableCell>
                <TableCell>Resolution</TableCell>
                <TableCell>Warning At</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : policies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No SLA policies configured</Typography>
                    {canModify && (
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenForm()}
                        sx={{ mt: 2 }}
                      >
                        Create First Policy
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                policies.map((policy) => (
                  <TableRow key={policy.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {policy.name}
                        </Typography>
                        {policy.is_default && (
                          <Chip label="Default" size="small" color="primary" sx={{ mt: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={TicketPriorityLabels[policy.priority]}
                        color={TicketPriorityColors[policy.priority]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {formatDuration(policy.first_response_hours)}
                    </TableCell>
                    <TableCell>
                      {formatDuration(policy.resolution_hours)}
                    </TableCell>
                    <TableCell>
                      {policy.warning_threshold_percent}%
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={policy.is_active ? 'Active' : 'Inactive'}
                        color={policy.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {canModify && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenForm(policy)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(policy)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Form Dialog */}
      <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editPolicy ? 'Edit SLA Policy' : 'Create SLA Policy'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Policy Name"
                required
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority"
                  onChange={(e) => handleFormChange('priority', e.target.value)}
                >
                  {TicketPriorityOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="First Response (hours)"
                value={formData.first_response_hours}
                onChange={(e) => handleFormChange('first_response_hours', parseInt(e.target.value) || 0)}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Resolution (hours)"
                value={formData.resolution_hours}
                onChange={(e) => handleFormChange('resolution_hours', parseInt(e.target.value) || 0)}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Warning Threshold (%)"
                value={formData.warning_threshold_percent}
                onChange={(e) => handleFormChange('warning_threshold_percent', parseInt(e.target.value) || 0)}
                inputProps={{ min: 1, max: 100 }}
                helperText="Show warning when this percentage of time has elapsed"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleFormChange('is_active', e.target.checked)}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_default}
                    onChange={(e) => handleFormChange('is_default', e.target.checked)}
                  />
                }
                label="Default for Priority"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Saving...' : (editPolicy ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete SLA Policy</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{policyToDelete?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SLAPolicyList;
